import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'

import { faCcAmex } from '@fortawesome/free-brands-svg-icons/faCcAmex'
import { faCcDiscover } from '@fortawesome/free-brands-svg-icons/faCcDiscover'
import { faCcMastercard } from '@fortawesome/free-brands-svg-icons/faCcMastercard'
import { faCcPaypal } from '@fortawesome/free-brands-svg-icons/faCcPaypal'
import { faCcVisa } from '@fortawesome/free-brands-svg-icons/faCcVisa'

import Grid from '@eig-builder/core-ui/Grid'
import Radio from '@eig-builder/core-ui/Radio'
import Typography from '@eig-builder/core-ui/Typography'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'
import useFetchAsJson from '@eig-builder/core-utils/hooks/useFetchAsJson'
import { clearCache } from '@eig-builder/core-utils/hooks/useDataApi'

import DataTable from 'core/components/data-table-without-shadow'

import { getCrmApiOptions } from '../../../../helpers/fetch-options-helper'
import TimeFormat from '../../../../helpers/time-format'
import { loadAccountById } from '../../../../store/actions'

import { ContentWrapper, ExpireDate, StyledIcon, StyledSkeleton } from './views'

const renderError = error => {
  if (!error) {
    return null
  }

  return error.message || error
}

const RemovePaymentMethodContent = ({ account, removeReq, setPaymentMethod }) => {
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('')

  const [paymentMethodsReq, getPaymentMethods] = useFetchAsJson(getCrmApiOptions(account, '/v1.0/billing/payment_methods'))

  const dispatch = useDispatch()

  const paymentMethods = useMemo(() => {
    const creditCards = paymentMethodsReq.data?.credit_cards || [] // eslint-disable-line
    const paypalAgreements = paymentMethodsReq.data?.paypal_billing_agreements || [] // eslint-disable-line
    return [...creditCards, ...paypalAgreements]
  }, [paymentMethodsReq.data])

  useEffect(() => {
    return () => clearCache() // Passing 'useCache: false' in the options of useFetchAsJson somehow didn't work
  }, [])

  useEffect(() => {
    if (removeReq.isSuccess) {
      setSelectedPaymentMethodId('')
      getPaymentMethods(getCrmApiOptions(account, '/v1.0/billing/payment_methods'))
      dispatch(loadAccountById(account.partnerId, account.id))
    }
  }, [removeReq.isSuccess])

  useEffect(() => {
    setPaymentMethod(selectedPaymentMethodId)
  }, [selectedPaymentMethodId])

  const handleRadioChange = e => {
    setSelectedPaymentMethodId(parseInt(e.target.value))
  }

  if (paymentMethodsReq.isLoading) {
    return <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledSkeleton animation='wave' variant='rect' height={40} />
        </Grid>
        <Grid item xs={12}>
          <StyledSkeleton animation='wave' variant='rect' height={40} />
        </Grid>
        <Grid item xs={12}>
          <StyledSkeleton animation='wave' variant='rect' height={40} />
        </Grid>
      </Grid>
    </>
  }

  if (paymentMethodsReq.isSuccess && paymentMethods.length === 0) {
    return <Typography variant='body1'>
      No payment methods found.
    </Typography>
  }

  if (paymentMethodsReq.isError || !paymentMethodsReq.data) {
    return <>
      <Typography variant='body1' gutterBottom>
        It looks like something went wrong...
      </Typography>
      <div>
        {renderError(paymentMethodsReq.error)}
      </div>
    </>
  }

  return <ContentWrapper>
    <Typography variant='body1' gutterBottom>
      Select a Credit Card or PayPal Billing Agreement:
    </Typography>
    <DataTable
      condensed
      headers={[
        {
          title: '',
          formatter: (_, row) => (
            <Radio
              checked={selectedPaymentMethodId === row.billing_info_id}
              onChange={handleRadioChange}
              value={row.billing_info_id}
              name='select-payment-method'
              dataElementLocation={DataElementLocations.POP_UP}
              dataElementLabel='cst-account--remove-payment-method--select-method'
              dataElementId='cst-account--remove-payment-method--select-method'
            />
          )
        },
        {
          title: 'Name',
          formatter: (_, row) => `${row.first_name || ''} ${row.last_name || ''}`
        },
        {
          title: 'Number',
          formatter: (_, row) => row.last_four_digits ? `**** **** **** ${row.last_four_digits}` : row.agreement_id
        },
        {
          title: 'Start date',
          formatter: (_, row) => row.start_date || row.created
            ? <TimeFormat date={row.start_date || row.created} />
            : ''
        },
        {
          title: 'Expiry',
          formatter: (_, row) => {
            const month = parseInt(row.expire?.slice(0, 2))
            let year = parseInt(row.expire?.slice(2, 4))
            let expired = false

            if (!isNaN(month) && !isNaN(year)) {
              year = year + 100
              const date = new Date()

              const expiredYear = year < date.getYear()
              const expiredMonth = year <= date.getYear() && month - 1 < date.getMonth()

              expired = expiredYear || expiredMonth
            }

            return <ExpireDate className={expired ? 'expired' : ''}>{row.expire}</ExpireDate>
          }
        },
        {
          title: 'Type',
          formatter: (_, row) => {
            let icon = row.agreement_id ? faCcPaypal : null

            switch (row.type) {
              case 'visa':
                icon = faCcVisa
                break
              case 'mastercard':
                icon = faCcMastercard
                break
              case 'discover':
                icon = faCcDiscover
                break
              case 'amex':
                icon = faCcAmex
                break
            }

            if (icon) {
              return <StyledIcon icon={icon} />
            }

            return row.type
          }
        },
        {
          title: 'Default',
          formatter: (_, row) => `${row.is_default}`
        }
      ]}
      rows={paymentMethods}
      tag='invoice-payment-service-items'
      detailClick={row => setSelectedPaymentMethodId(row.billing_info_id)}
      detailUrl='detail' // only used to prevent the 'no-pointer' class being set on every column
    />
  </ContentWrapper>
}

RemovePaymentMethodContent.propTypes = {
  account: PropTypes.object.isRequired,
  removeReq: PropTypes.object.isRequired,
  setPaymentMethod: PropTypes.func.isRequired
}

export default RemovePaymentMethodContent
